import type { PreCredentialRequirementDTO } from "../interface/precredential";

export type PreCredentialItemKind = "fixed_field" | "reference" | "identity_document" | "document" | "profile_image";

export type OptionalDocumentGroup = "identity" | "compliance" | "qualifications";

export type PreCredentialPlatformCatalogItem = {
  requirementKey: string;
  requirementLabel: string;
  itemType: string;
  description: string;
  kind: PreCredentialItemKind;
  locked?: boolean;
  /** General settings step only — not shown in core requirements list */
  generalSetting?: boolean;
  /** Hidden in hospital settings UI — can be re-enabled in catalog later */
  hiddenInSettings?: boolean;
  aliases?: string[];
  optionalGroup?: OptionalDocumentGroup;
};

/** Active core requirements shown in settings and saved to the hospital profile. */
export const ACTIVE_CORE_REQUIREMENT_KEYS = [
  "AHPRA_NUMBER",
  "PRIMARY_ID_PROOF",
  "REFERENCES",
  "medicareCard",
  "medicalDegree",
] as const;

export const isHiddenInSettings = (item: PreCredentialPlatformCatalogItem) =>
  !!item.hiddenInSettings;

export const isActiveCoreRequirementKey = (requirementKey: string) =>
  ACTIVE_CORE_REQUIREMENT_KEYS.includes(
    requirementKey as (typeof ACTIVE_CORE_REQUIREMENT_KEYS)[number],
  );

/** General hospital policy toggles (General settings step). */
export const GENERAL_PLATFORM_SETTINGS: PreCredentialPlatformCatalogItem[] = [
  {
    requirementKey: "REAL_DOCTOR_PICTURE",
    requirementLabel: "Require real doctor picture",
    itemType: "profile_image",
    kind: "profile_image",
    locked: true,
    generalSetting: true,
    hiddenInSettings: true,
    description:
      "Doctors must have a real profile photo on their StatDoctor profile before they can apply (not a placeholder avatar).",
  },
];

/** Always included — active core rows shown in settings. */
export const LOCKED_PLATFORM_REQUIREMENTS: PreCredentialPlatformCatalogItem[] = [
  {
    requirementKey: "AHPRA_NUMBER",
    requirementLabel: "AHPRA number",
    itemType: "fixed_field",
    kind: "fixed_field",
    locked: true,
    description:
      "The doctor's AHPRA registration number from their StatDoctor profile. This is a profile field, not a file upload.",
  },
  {
    requirementKey: "PRIMARY_ID_PROOF",
    requirementLabel: "First ID proof",
    itemType: "identity_document",
    kind: "identity_document",
    locked: true,
    description:
      "The doctor's primary identification document from their profile (passport, driver's licence, etc.).",
    aliases: ["primaryDocument", "primary_id"],
  },
  {
    requirementKey: "REFERENCES",
    requirementLabel: "References",
    itemType: "reference",
    kind: "reference",
    locked: true,
    description:
      "Professional references on the doctor's profile. This is not a document upload — references are reviewed from profile data.",
  },
  {
    requirementKey: "medicareCard",
    requirementLabel: "Medicare card",
    itemType: "document",
    kind: "document",
    locked: true,
    description: "Medicare card copy from the doctor's profile.",
  },
  {
    requirementKey: "medicalDegree",
    requirementLabel: "Medical degree",
    itemType: "document",
    kind: "document",
    locked: true,
    description: "Medical degree or qualification certificate from the doctor's profile.",
  },
];

/** Retained in catalog for future reactivation — not shown in settings UI. */
export const HIDDEN_LOCKED_PLATFORM_REQUIREMENTS: PreCredentialPlatformCatalogItem[] = [
  {
    requirementKey: "RESUME",
    requirementLabel: "Resume",
    itemType: "document",
    kind: "document",
    locked: true,
    hiddenInSettings: true,
    description: "The doctor's resume or CV already uploaded to their profile.",
  },
];

/**
 * Optional platform documents — every doctor profile file type the backend indexes
 * (see HospitalPreCredentialServiceImpl.buildDoctorFiles). Referral is excluded in backend.
 */
export const OPTIONAL_PLATFORM_REQUIREMENTS: PreCredentialPlatformCatalogItem[] = [
  {
    requirementKey: "OTHER_ID_PROOFS",
    requirementLabel: "Secondary ID",
    itemType: "identity_document",
    kind: "identity_document",
    optionalGroup: "identity",
    hiddenInSettings: true,
    description: "",
    aliases: ["otherDocument", "other_document", "other id"],
  },
  {
    requirementKey: "THREE_ID_PROOF",
    requirementLabel: "Third ID",
    itemType: "identity_document",
    kind: "identity_document",
    optionalGroup: "identity",
    hiddenInSettings: true,
    description: "",
    aliases: ["threeDocument", "thirdDocument", "third id"],
  },
  {
    requirementKey: "policeCheck",
    requirementLabel: "Police check",
    itemType: "document",
    kind: "document",
    optionalGroup: "compliance",
    hiddenInSettings: true,
    description: "National police check uploaded to the doctor's profile.",
  },
  {
    requirementKey: "criminalHistoryCheck",
    requirementLabel: "Criminal history check",
    itemType: "document",
    kind: "document",
    optionalGroup: "compliance",
    hiddenInSettings: true,
    description: "Criminal history check document from the doctor's profile.",
  },
  {
    requirementKey: "workingWithChildrenCheck",
    requirementLabel: "Working with children check",
    itemType: "document",
    kind: "document",
    optionalGroup: "compliance",
    hiddenInSettings: true,
    description: "WWCC / working with children clearance from the doctor's profile.",
    aliases: ["wwcc"],
  },
  {
    requirementKey: "vaccinationCertificate",
    requirementLabel: "Vaccination certificate",
    itemType: "document",
    kind: "document",
    optionalGroup: "compliance",
    hiddenInSettings: true,
    description: "Immunisation / vaccination certificate from the doctor's profile.",
  },
  {
    requirementKey: "workVisa",
    requirementLabel: "Work visa",
    itemType: "document",
    kind: "document",
    optionalGroup: "compliance",
    hiddenInSettings: true,
    description: "Work visa or right-to-work document from the doctor's profile.",
  },
  {
    requirementKey: "approvalForSecondaryEmployment",
    requirementLabel: "Approval for secondary employment",
    itemType: "document",
    kind: "document",
    optionalGroup: "qualifications",
    hiddenInSettings: true,
    description: "Hospital approval for secondary employment, if on the doctor's profile.",
  },
];

export const OPTIONAL_PLATFORM_GROUP_LABELS: Record<OptionalDocumentGroup, string> = {
  identity: "Identity documents",
  compliance: "Compliance documents",
  qualifications: "Qualifications",
};

export const OPTIONAL_PLATFORM_GROUP_ORDER: OptionalDocumentGroup[] = [
  "identity",
  "compliance",
  "qualifications",
];

export const ALL_PLATFORM_CATALOG = [
  ...GENERAL_PLATFORM_SETTINGS,
  ...LOCKED_PLATFORM_REQUIREMENTS,
  ...HIDDEN_LOCKED_PLATFORM_REQUIREMENTS,
  ...OPTIONAL_PLATFORM_REQUIREMENTS,
];

export const VISIBLE_SETTINGS_WIZARD_STEPS = [
  "general",
  "always-included",
] as const;

export const PLATFORM_KIND_LABELS: Record<PreCredentialItemKind, string> = {
  fixed_field: "Profile field",
  reference: "Reference",
  identity_document: "Identity document",
  document: "Document",
  profile_image: "Profile photo",
};

const normalizeKey = (value: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

export const resolveCatalogItem = (
  requirementKey: string,
  requirementLabel?: string,
): PreCredentialPlatformCatalogItem | undefined => {
  const keyNorm = normalizeKey(requirementKey);
  const labelNorm = normalizeKey(requirementLabel || "");

  return ALL_PLATFORM_CATALOG.find((item) => {
    if (normalizeKey(item.requirementKey) === keyNorm) return true;
    if (item.aliases?.some((alias) => normalizeKey(alias) === keyNorm)) return true;
    if (labelNorm && normalizeKey(item.requirementLabel) === labelNorm) return true;
    return false;
  });
};

export const filterVisibleSettingsRequirements = (
  saved: PreCredentialRequirementDTO[],
): PreCredentialRequirementDTO[] =>
  saved.filter((row) => {
    const match = resolveCatalogItem(
      String(row.requirementKey || ""),
      String(row.requirementLabel || ""),
    );
    if (!match) return false;
    if (match.hiddenInSettings || match.generalSetting) return false;
    if (match.locked) return isActiveCoreRequirementKey(match.requirementKey);
    return !match.hiddenInSettings;
  });

export const getEditableDescription = (
  catalog: PreCredentialPlatformCatalogItem,
  row?: Partial<PreCredentialRequirementDTO> | null,
) => {
  const custom = String(row?.hospitalNote || row?.description || "").trim();
  return custom || catalog.description;
};

export const catalogItemToRequirement = (
  item: PreCredentialPlatformCatalogItem,
  sortOrder: number,
  editableDescription?: string,
): PreCredentialRequirementDTO => {
  const note = String(editableDescription || "").trim();
  return {
    itemType: item.itemType,
    requirementKey: item.requirementKey,
    requirementLabel: item.requirementLabel,
    description: note || item.description,
    required: true,
    editableByHospital: true,
    hospitalNote: note,
    sortOrder,
  };
};

export type LockedRequirementOverrides = Record<
  string,
  { description: string }
>;

export type GeneralSettingsState = {
  requireRealDoctorPhoto: boolean;
};

export const REAL_DOCTOR_PICTURE_KEY = "REAL_DOCTOR_PICTURE";

export const buildLockedRequirements = (
  overrides: LockedRequirementOverrides = {},
): PreCredentialRequirementDTO[] =>
  LOCKED_PLATFORM_REQUIREMENTS.map((item, index) =>
    catalogItemToRequirement(
      item,
      index + 1,
      overrides[item.requirementKey]?.description,
    ),
  );

export const isOptionalPlatformSelected = (
  saved: PreCredentialRequirementDTO[],
  catalogItem: PreCredentialPlatformCatalogItem,
) =>
  saved.some((row) => {
    const match = resolveCatalogItem(
      String(row.requirementKey || ""),
      String(row.requirementLabel || ""),
    );
    return match?.requirementKey === catalogItem.requirementKey;
  });

export const splitSavedRequirements = (saved: PreCredentialRequirementDTO[]) => {
  const optionalSelected: PreCredentialRequirementDTO[] = [];
  const lockedOverrides: LockedRequirementOverrides = {};
  const requireRealDoctorPhoto = false;

  filterVisibleSettingsRequirements(saved).forEach((row) => {
    const match = resolveCatalogItem(
      String(row.requirementKey || ""),
      String(row.requirementLabel || ""),
    );
    if (!match || match.hiddenInSettings || match.generalSetting) return;

    if (match.locked && isActiveCoreRequirementKey(match.requirementKey)) {
      lockedOverrides[match.requirementKey] = {
        description: getEditableDescription(match, row),
      };
      return;
    }

    if (optionalSelected.some((item) => item.requirementKey === match.requirementKey)) return;
    optionalSelected.push(
      catalogItemToRequirement(
        match,
        optionalSelected.length + LOCKED_PLATFORM_REQUIREMENTS.length + 1,
        getEditableDescription(match, row),
      ),
    );
  });

  return { optionalSelected, lockedOverrides, requireRealDoctorPhoto };
};

export const mergeRequirementsForSave = (
  optionalSelected: PreCredentialRequirementDTO[],
  lockedOverrides: LockedRequirementOverrides = {},
  _generalSettings: GeneralSettingsState = { requireRealDoctorPhoto: false },
): PreCredentialRequirementDTO[] => {
  const locked = buildLockedRequirements(lockedOverrides);
  const optional = optionalSelected
    .map((row, index) => {
      const catalog = resolveCatalogItem(row.requirementKey, row.requirementLabel);
      if (
        !catalog ||
        catalog.locked ||
        catalog.generalSetting ||
        catalog.hiddenInSettings
      ) {
        return null;
      }
      return catalogItemToRequirement(
        catalog,
        locked.length + index + 1,
        getEditableDescription(catalog, row),
      );
    })
    .filter(Boolean) as PreCredentialRequirementDTO[];

  return [...locked, ...optional];
};

export const toggleOptionalPlatform = (
  saved: PreCredentialRequirementDTO[],
  catalogItem: PreCredentialPlatformCatalogItem,
  selected: boolean,
): PreCredentialRequirementDTO[] => {
  const without = saved.filter((row) => {
    const match = resolveCatalogItem(row.requirementKey || "", row.requirementLabel || "");
    return match?.requirementKey !== catalogItem.requirementKey;
  });
  if (!selected) return without;

  const existing = saved.find((row) => {
    const match = resolveCatalogItem(row.requirementKey || "", row.requirementLabel || "");
    return match?.requirementKey === catalogItem.requirementKey;
  });

  return [
    ...without,
    catalogItemToRequirement(
      catalogItem,
      without.length + LOCKED_PLATFORM_REQUIREMENTS.length + 1,
      existing ? getEditableDescription(catalogItem, existing) : catalogItem.description,
    ),
  ];
};

export const updateOptionalDescription = (
  saved: PreCredentialRequirementDTO[],
  catalogItem: PreCredentialPlatformCatalogItem,
  description: string,
): PreCredentialRequirementDTO[] =>
  saved.map((row) => {
    const match = resolveCatalogItem(row.requirementKey || "", row.requirementLabel || "");
    if (match?.requirementKey !== catalogItem.requirementKey) return row;
    return catalogItemToRequirement(catalogItem, row.sortOrder || 0, description);
  });

export const isCustomExtraDocument = (row: PreCredentialRequirementDTO) =>
  !resolveCatalogItem(String(row.requirementKey || ""), String(row.requirementLabel || ""));

export const normalizeCustomExtraDocuments = (
  saved: PreCredentialRequirementDTO[],
): PreCredentialRequirementDTO[] =>
  saved
    .filter(isCustomExtraDocument)
    .map((row, index) => ({
      itemType: "document",
      requirementKey: String(row.requirementKey || `extra_${index + 1}`).trim(),
      requirementLabel: String(row.requirementLabel || "").trim(),
      description: String(row.description || row.hospitalNote || "").trim(),
      required: false,
      editableByHospital: true,
      hospitalNote: String(row.hospitalNote || row.description || "").trim(),
      sortOrder: index + 1,
    }))
    .filter((row) => row.requirementLabel);

export const getOptionalByGroup = (group: OptionalDocumentGroup) =>
  OPTIONAL_PLATFORM_REQUIREMENTS.filter(
    (item) => item.optionalGroup === group && !item.hiddenInSettings,
  );
